import Phaser from 'phaser';
import { GAME_CONFIG } from '../../config/game';
import { BaseRunScene, RunSceneConfig } from './BaseRunScene';

/**
 * ClothesChaoScene (Level 1-2)
 * 
 * Theme: Morning clothes chaos - flying clothes as obstacles
 * Goal: Collect ALL 5 outfit pieces while dodging flying laundry
 * Win: Collect all pieces before running out of health
 * Fail: Run out of health OR reach end without all pieces
 * 
 * Uses "Bag" spawning system to ensure all pieces appear fairly.
 */
export class ClothesChaoScene extends BaseRunScene {
  private spawnTimer?: Phaser.Time.TimerEvent;
  private collectTimer?: Phaser.Time.TimerEvent;
  private outfitPieces: string[] = ['shirt', 'pants', 'sock1', 'sock2', 'shoes'];
  private collectedPieces: Set<string> = new Set();
  private outfitUI?: Phaser.GameObjects.Container;
  private pieceIcons: Map<string, Phaser.GameObjects.Text> = new Map();
  
  // "Bag" spawning system - ensures fair distribution
  private spawnBag: string[] = [];
  
  // Grace period - no obstacles at start
  private readonly GRACE_PERIOD = 3000; // 3 seconds
  private graceActive: boolean = true;

  constructor() {
    const config: RunSceneConfig = {
      levelId: '1-2',
      levelName: 'Clothes Chaos',
      backgroundKey: '1-2_clothes_chaos',
      targetDistance: 1500, // Reasonable level length
      scrollSpeed: 100, // Manageable speed
    };
    super('ClothesChaoScene', config);
  }

  /**
   * Check if scene is still valid for operations
   */
  private isSceneValid(): boolean {
    return this.scene && this.scene.isActive(this.scene.key) && !this.isGameOver;
  }

  /**
   * Safe delayed call that checks scene validity
   */
  private safeDelayedCall(delay: number, callback: () => void): Phaser.Time.TimerEvent {
    return this.time.delayedCall(delay, () => {
      if (this.isSceneValid()) {
        callback();
      }
    });
  }

  create() {
    super.create();
    this.collectedPieces = new Set();
    this.pieceIcons = new Map();
    this.graceActive = true; // Reset grace period
    
    // Initialize the spawn bag with all pieces
    this.refillSpawnBag();
    
    // Create outfit indicator UI
    this.createOutfitUI();
    
    // Spawn first collectible immediately
    this.safeDelayedCall(500, () => this.spawnCollectible());
  }

  /**
   * Refill the spawn bag with uncollected pieces and shuffle
   */
  private refillSpawnBag() {
    // Get all pieces not yet collected
    const uncollected = this.outfitPieces.filter(p => !this.collectedPieces.has(p));
    
    // Shuffle using Fisher-Yates
    this.spawnBag = [...uncollected];
    for (let i = this.spawnBag.length - 1; i > 0; i--) {
      const j = Phaser.Math.Between(0, i);
      [this.spawnBag[i], this.spawnBag[j]] = [this.spawnBag[j], this.spawnBag[i]];
    }
  }

  /**
   * Get next piece from the bag (refills if empty)
   */
  private getNextPieceFromBag(): string | null {
    // If bag is empty, refill it
    if (this.spawnBag.length === 0) {
      this.refillSpawnBag();
    }
    
    // If still empty (all collected), return null
    if (this.spawnBag.length === 0) {
      return null;
    }
    
    // Pop from the bag
    return this.spawnBag.pop() || null;
  }

  private createOutfitUI() {
    if (!this.isSceneValid()) return;
    
    const { width } = GAME_CONFIG;
    
    this.outfitUI = this.add.container(width / 2, 85);
    this.outfitUI.setDepth(100);
    
    // Background
    const bg = this.add.rectangle(0, 0, 200, 35, 0x000000, 0.5);
    bg.setStrokeStyle(1, 0xffd700);
    this.outfitUI.add(bg);
    
    // Label
    const label = this.add.text(0, -8, 'Collect the outfit:', {
      fontFamily: 'Georgia, serif',
      fontSize: '10px',
      color: '#ffffff',
    }).setOrigin(0.5);
    this.outfitUI.add(label);
    
    // Outfit piece icons
    const icons: Record<string, string> = {
      'shirt': '👕',
      'pants': '👖',
      'sock1': '🧦',
      'sock2': '🧦',
      'shoes': '👟',
    };
    
    let xPos = -80;
    for (const piece of this.outfitPieces) {
      const icon = this.add.text(xPos, 8, icons[piece], {
        fontSize: '16px',
      }).setOrigin(0.5);
      icon.setAlpha(0.3); // Grayed out until collected
      this.pieceIcons.set(piece, icon);
      this.outfitUI.add(icon);
      xPos += 40;
    }
  }

  protected startSpawning() {
    if (!this.isSceneValid()) return;
    
    // Start with grace period - no obstacles for first few seconds
    this.graceActive = true;
    this.safeDelayedCall(this.GRACE_PERIOD, () => {
      this.graceActive = false;
    });
    
    // Spawn obstacles at slower rate (1500ms instead of 1000ms)
    this.spawnTimer = this.time.addEvent({
      delay: 1500,
      callback: () => {
        if (this.isSceneValid() && !this.graceActive) {
          this.spawnObstacle();
        }
      },
      callbackScope: this,
      loop: true,
    });

    // Spawn collectibles regularly - ensures all pieces can be collected
    this.collectTimer = this.time.addEvent({
      delay: 1200, // Slightly faster collectibles
      callback: () => {
        if (this.isSceneValid()) this.spawnCollectible();
      },
      callbackScope: this,
      loop: true,
    });
  }

  protected spawnObstacle() {
    if (!this.isSceneValid() || this.isPaused || this.graceActive) return;

    const { width, height } = GAME_CONFIG;
    
    // Spawn only 1 obstacle at a time (was 1-2)
    const y = Phaser.Math.Between(height - 300, height - 100);
    
    const obstacle = this.add.graphics();
    const clothType = Phaser.Math.Between(0, 2);
    
    if (clothType === 0) {
      // T-shirt shape
      obstacle.fillStyle(Phaser.Math.Between(0, 1) ? 0xff6b6b : 0x4ecdc4, 1);
      obstacle.fillRect(-15, -10, 30, 25);
      obstacle.fillRect(-25, -10, 10, 15);
      obstacle.fillRect(15, -10, 10, 15);
    } else if (clothType === 1) {
      // Pants shape
      obstacle.fillStyle(0x4a90d9, 1);
      obstacle.fillRect(-12, -15, 24, 15);
      obstacle.fillRect(-12, 0, 10, 20);
      obstacle.fillRect(2, 0, 10, 20);
    } else {
      // Sock shape
      obstacle.fillStyle(0xffffff, 1);
      obstacle.fillRoundedRect(-8, -12, 16, 24, 4);
    }

    const container = this.add.container(width + 30, y, [obstacle]);
    this.physics.add.existing(container);
    
    const body = container.body as Phaser.Physics.Arcade.Body;
    body.setSize(30, 30);
    body.setAllowGravity(false);
    
    this.obstacles.add(container);

    // Spinning animation
    this.tweens.add({
      targets: obstacle,
      rotation: Math.PI * 2,
      duration: 800 + Phaser.Math.Between(0, 400),
      repeat: -1,
    });
  }

  protected spawnCollectible() {
    if (!this.isSceneValid() || this.isPaused) return;
    if (this.collectedPieces.size >= this.outfitPieces.length) return;

    const { width, height } = GAME_CONFIG;
    const y = Phaser.Math.Between(height - 320, height - 140);

    // Get next piece from the bag (guarantees fair distribution)
    const piece = this.getNextPieceFromBag();
    if (!piece) return;

    // Create collectible with distinct golden glow
    const glow = this.add.graphics();
    glow.fillStyle(0xffd700, 0.4);
    glow.fillCircle(0, 0, 30);
    
    const collectible = this.add.graphics();
    collectible.fillStyle(0xffd700, 1);
    
    // Draw based on piece type
    if (piece === 'shirt') {
      collectible.fillRect(-12, -8, 24, 18);
      collectible.fillRect(-18, -8, 6, 12);
      collectible.fillRect(12, -8, 6, 12);
    } else if (piece === 'pants') {
      collectible.fillRect(-10, -12, 20, 12);
      collectible.fillRect(-10, 0, 8, 15);
      collectible.fillRect(2, 0, 8, 15);
    } else if (piece.startsWith('sock')) {
      collectible.fillRoundedRect(-6, -10, 12, 20, 3);
    } else if (piece === 'shoes') {
      collectible.fillRoundedRect(-10, -5, 20, 10, 3);
    }

    const container = this.add.container(width + 30, y, [glow, collectible]);
    container.setData('piece', piece);
    
    this.physics.add.existing(container);
    const body = container.body as Phaser.Physics.Arcade.Body;
    body.setSize(60, 60); // Larger hitbox for easier pickup (was 45x45)
    body.setAllowGravity(false);
    
    this.collectibles.add(container);

    // Pulsing glow animation
    this.tweens.add({
      targets: glow,
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0.2,
      duration: 400,
      yoyo: true,
      repeat: -1,
    });

    // Floating animation
    this.tweens.add({
      targets: container,
      y: y - 15,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  // Override to get piece data BEFORE destruction
  protected handleCollectiblePickup(
    _player: Phaser.GameObjects.GameObject,
    collectible: Phaser.GameObjects.GameObject
  ) {
    // Guard against processing during game over
    if (!this.isSceneValid() || this.isPaused) return;
    
    // Get the piece type BEFORE destroying
    const piece = collectible.getData('piece') as string;
    
    // Destroy the collectible
    collectible.destroy();
    this.collected++;
    
    // Guard again after destruction
    if (this.isGameOver || !piece) return;
    
    // Skip if already collected (shouldn't happen with bag system)
    if (this.collectedPieces.has(piece)) return;
    
    // Mark as collected
    this.collectedPieces.add(piece);
    
    // Update UI - highlight collected piece
    const icon = this.pieceIcons.get(piece);
    if (icon && icon.scene) {
      icon.setAlpha(1);
      // Bounce animation
      this.tweens.add({
        targets: icon,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 150,
        yoyo: true,
      });
    }
    
    // Show collection feedback
    this.showCollectFeedback(piece);
    
    // Check if outfit complete - WIN!
    if (this.collectedPieces.size >= this.outfitPieces.length) {
      this.showOutfitComplete();
    }
  }

  // Required by BaseRunScene but not used since we override handleCollectiblePickup
  protected onCollect() {
    // Handled in handleCollectiblePickup override
  }

  private showCollectFeedback(piece: string) {
    if (!this.isSceneValid()) return;
    
    const { width } = GAME_CONFIG;
    
    const icons: Record<string, string> = {
      'shirt': '👕',
      'pants': '👖',
      'sock1': '🧦',
      'sock2': '🧦',
      'shoes': '👟',
    };

    const text = this.add.text(width / 2, 130, `${icons[piece]} Got it! (${this.collectedPieces.size}/5)`, {
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(150);

    this.tweens.add({
      targets: text,
      y: 110,
      alpha: 0,
      duration: 800,
      onComplete: () => {
        if (text && text.scene) text.destroy();
      },
    });
  }

  private showOutfitComplete() {
    if (!this.isSceneValid()) return;
    
    const { width, height } = GAME_CONFIG;
    
    // Stop spawning
    this.spawnTimer?.destroy();
    this.collectTimer?.destroy();
    
    // Big celebration text
    const completeText = this.add.text(width / 2, height / 2, '👕👖🧦🧦👟\nOUTFIT COMPLETE!', {
      fontFamily: 'Georgia, serif',
      fontSize: '24px',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 4,
      align: 'center',
    }).setOrigin(0.5).setDepth(200);

    this.tweens.add({
      targets: completeText,
      scale: { from: 0.5, to: 1.2 },
      duration: 500,
      yoyo: true,
      onComplete: () => {
        if (!this.isSceneValid()) {
          if (completeText && completeText.scene) completeText.destroy();
          return;
        }
        this.tweens.add({
          targets: completeText,
          alpha: 0,
          delay: 500,
          duration: 500,
          onComplete: () => {
            if (completeText && completeText.scene) completeText.destroy();
            // Trigger victory
            if (this.isSceneValid()) {
              this.config.targetDistance = this.distance;
            }
          },
        });
      },
    });
  }

  protected updateLevel(_time: number, _delta: number) {
    // Increase speed over time, but cap it
    this.scrollSpeed = Math.min(200, this.config.scrollSpeed + (this.distance / 20));
    
    // Check fail condition - reached target without all pieces
    if (this.distance >= this.config.targetDistance * 0.90 && this.collectedPieces.size < this.outfitPieces.length) {
      // Show warning if close to end
      if (!this.data.get('warnedNearEnd')) {
        this.data.set('warnedNearEnd', true);
        this.showWarning();
      }
    }
  }

  private showWarning() {
    if (!this.isSceneValid()) return;
    
    const { width } = GAME_CONFIG;
    const missing = this.outfitPieces.length - this.collectedPieces.size;
    
    const warning = this.add.text(width / 2, 150, `⚠️ Still need ${missing} piece${missing > 1 ? 's' : ''}!`, {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ff6666',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(200);

    this.tweens.add({
      targets: warning,
      alpha: { from: 1, to: 0.5 },
      duration: 300,
      yoyo: true,
      repeat: 3,
      onComplete: () => {
        if (warning && warning.scene) warning.destroy();
      },
    });
  }

  // Clean up on shutdown
  shutdown() {
    super.shutdown();
    this.spawnTimer?.destroy();
    this.collectTimer?.destroy();
    this.spawnTimer = undefined;
    this.collectTimer = undefined;
  }
}
